import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { PushNotificationService } from 'src/app/services/push-notification.service';

/**
 * Modal that asks the user to enable Web Push notifications for the support
 * chat. Opened from `SupportChatComponent` every time the user lands on the
 * chat page while the browser permission is still `default`. We deliberately
 * keep nagging on every visit until the user accepts: dismissing only closes
 * the modal — the next time they open the chat, it shows again. Once the
 * native permission flips to `granted` or `denied`, the parent's guard stops
 * opening the modal.
 */
@Component({
  standalone: true,
  selector: 'app-push-permission-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatDialogModule, MatButtonModule],
  template: `
    <div class="push-dlg">
      <div class="push-dlg__icon" aria-hidden="true">
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
      </div>
      <h2 class="push-dlg__title">Activa los avisos del soporte</h2>
      <p class="push-dlg__copy">
        Te enviaremos una notificación al navegador cuando soporte te
        responda, aunque tengas SuperPay cerrado.
      </p>
      <div class="push-dlg__actions">
        <button
          type="button"
          class="push-dlg__btn push-dlg__btn--primary"
          [disabled]="busy()"
          (click)="enable()"
        >
          {{ busy() ? 'Activando…' : 'Activar avisos' }}
        </button>
        <button
          type="button"
          class="push-dlg__btn push-dlg__btn--ghost"
          [disabled]="busy()"
          (click)="dismiss()"
        >
          Ahora no
        </button>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      .push-dlg {
        padding: 24px 24px 20px;
        max-width: 360px;
        text-align: center;
      }
      .push-dlg__icon {
        width: 48px;
        height: 48px;
        border-radius: 999px;
        background: rgba(37, 99, 235, 0.1);
        color: #2563eb;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 14px;
      }
      .push-dlg__title {
        font-size: 17px;
        font-weight: 600;
        color: #0f172a;
        margin: 0 0 8px;
        line-height: 1.25;
      }
      .push-dlg__copy {
        font-size: 13.5px;
        line-height: 1.45;
        color: #475569;
        margin: 0 0 20px;
      }
      .push-dlg__actions {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      .push-dlg__btn {
        height: 42px;
        border: 0;
        border-radius: 10px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: background 120ms ease, color 120ms ease;
      }
      .push-dlg__btn:disabled {
        opacity: 0.6;
        cursor: progress;
      }
      .push-dlg__btn--primary {
        background: #2563eb;
        color: #ffffff;
      }
      .push-dlg__btn--primary:hover:not(:disabled) {
        background: #1d4ed8;
      }
      .push-dlg__btn--ghost {
        background: transparent;
        color: #64748b;
      }
      .push-dlg__btn--ghost:hover:not(:disabled) {
        color: #0f172a;
      }
    `
  ]
})
export class PushPermissionDialogComponent {
  private readonly push = inject(PushNotificationService);
  private readonly dialogRef = inject(MatDialogRef<PushPermissionDialogComponent, boolean>);

  readonly busy = signal(false);

  async enable() {
    this.busy.set(true);
    try {
      const granted = await this.push.enable();
      this.dialogRef.close(granted);
    } finally {
      this.busy.set(false);
    }
  }

  dismiss() {
    this.dialogRef.close(false);
  }
}
